// qs-BrushSizePanel.vue
<template>
  <div class="brush-size-panel">
    <div
      v-for="(item, index) in brushItems"
      :key="item.id"
      class="brush-item"
      :class="{ active: selectedIndex === index }"
      @click="(e) => handleClick(e, index)"
    >
      <div
        class="preview-square"
        :class="{
          'active-preview': selectedIndex === index,
          'draggable': selectedIndex === index
        }"
        @mousedown="(e) => selectedIndex === index && handleMouseDown(e, index)"
        @touchstart.passive="(e) => selectedIndex === index && handleTouchStart(e, index)"
      >
        <div
          class="preview-circle"
          :style="{
            width: `${Math.min(item.size, 32)}px`,
            height: `${Math.min(item.size, 32)}px`
          }"
        />
      </div>
      <input
        v-if="selectedIndex === index"
        v-model="inputValues[index]"
        type="number"
        min="1"
        class="input"
        @blur="handleInputBlur(index)"
        @focus="cursorManager.setTextCursor()"
      >
      <span
        v-else
        class="size-text"
      >{{ item.size }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BRUSH_SIZES, DEFAULT_BRUSH_INDEX, DEFAULT_BRUSH_SIZE, initializeBrushPanel, MAX_BRUSH_ITEMS } from '@/config/qs-brushDefaults'
import { useToolStore } from '@/stores/qs-store-tools'
import { cursorManager } from '@/utils/qs-cursorManager'
import { onMounted, onUnmounted, ref, watch } from 'vue'

interface BrushItem {
  readonly id: string
  size: number
}

interface DragState {
  isDragging: boolean
  startX: number
  startY: number
  startSize: number
  centerX: number
  centerY: number
  timeout: number | null
  mouseMoveHandler?: (e: MouseEvent) => void
  mouseUpHandler?: () => void
  touchMoveHandler?: (e: TouchEvent) => void
  touchEndHandler?: () => void
}

interface Props {
  brushCount?: number
  initialSizes?: number[]
  maxItems?: number
}

interface CustomEventDetail {
  index: number
}

const props = withDefaults(defineProps<Props>(), {
  brushCount: MAX_BRUSH_ITEMS,
  initialSizes: () => [...BRUSH_SIZES],
  maxItems: MAX_BRUSH_ITEMS
})

const emit = defineEmits<{
  'brush-size-changed': [size: number, index: number]
  'brush-item-selected': [index: number]
}>()

const toolStore = useToolStore()
const brushItems = ref<BrushItem[]>([])
const inputValues = ref<number[]>([])
const selectedIndex = ref(DEFAULT_BRUSH_INDEX)
const isInitialized = ref(false)
const dragStates = ref<DragState[]>([])

const initializeItems = (): void => {
  const sizes = [2, 4, 8, 16, 32]

  brushItems.value = sizes.map((size, index) => ({
    id: `brush-item-${index}`,
    size
  }))
  inputValues.value = [...sizes]

  dragStates.value = sizes.map(() => ({
    isDragging: false,
    startX: 0,
    startY: 0,
    startSize: 0,
    centerX: 0,
    centerY: 0,
    timeout: null
  }))

  selectedIndex.value = 2
  toolStore.setBrushSize(8)
}

const syncWithToolStore = (): void => {
  const currentSize = Number(toolStore.currentTool.size)
  const matchingIndex = brushItems.value.findIndex(item => item.size === currentSize)

  if (matchingIndex !== -1) {
    selectedIndex.value = matchingIndex
    inputValues.value[matchingIndex] = currentSize
    const matchingItem = brushItems.value[matchingIndex]
    if (matchingItem) {
      matchingItem.size = currentSize
    }
  } else {
    selectedIndex.value = DEFAULT_BRUSH_INDEX
    inputValues.value[DEFAULT_BRUSH_INDEX] = currentSize
    const defaultItem = brushItems.value[DEFAULT_BRUSH_INDEX]
    if (defaultItem) {
      defaultItem.size = currentSize
    }
  }
}

const selectItem = (index: number): void => {
  selectedIndex.value = index
  const newSize = brushItems.value[index]?.size

  if (newSize !== undefined) {
    toolStore.setBrushSize(newSize)
    emit('brush-item-selected', index)
    emit('brush-size-changed', newSize, index)
  }
}

const validateInput = (index: number): void => {
  const inputValue = inputValues.value[index]
  const value = Math.max(1, inputValue || 1)
  inputValues.value[index] = value
  const brushItem = brushItems.value[index]
  if (brushItem) {
    brushItem.size = value
  }

  toolStore.setBrushSize(value)
  emit('brush-size-changed', value, index)
}

const handleInputBlur = (index: number): void => {
  validateInput(index)
  cursorManager.resetToDrawing()
}

const handleMouseDown = (event: MouseEvent, index: number): void => {
  event.preventDefault()
  startDragInteraction(event, index, event.clientX, event.clientY)
}

const handleTouchStart = (event: TouchEvent, index: number): void => {
  event.preventDefault()
  const touch = event.touches[0]
  if (touch) {
    startDragInteraction(event, index, touch.clientX, touch.clientY)
  }
}

const handleClick = (_event: MouseEvent, index: number): void => {
  const dragState = dragStates.value[index]
  if (dragState && !dragState.isDragging) {
    selectItem(index)
  }
}

const startDragInteraction = (event: MouseEvent | TouchEvent, index: number, clientX: number, clientY: number): void => {
  const dragState = dragStates.value[index]
  if (!dragState) return

  if (dragState.timeout) {
    clearTimeout(dragState.timeout)
  }

  dragState.timeout = window.setTimeout(() => {
    dragState.isDragging = true

    const rect = (event.target as HTMLElement).getBoundingClientRect()
    dragState.centerX = rect.left + rect.width / 2
    dragState.centerY = rect.top + rect.height / 2

    dragState.startX = clientX
    dragState.startY = clientY
    dragState.startSize = brushItems.value[index]?.size || 1

    const handleMouseMove = (e: MouseEvent) => handleDrag(e, index)
    const handleMouseUp = () => endDrag(index)
    const handleTouchMove = (e: TouchEvent) => handleDrag(e, index)
    const handleTouchEnd = () => endDrag(index)

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp, { passive: true })

    if ('ontouchstart' in window) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false })
      document.addEventListener('touchend', handleTouchEnd, { passive: true })
    }

    dragState.mouseMoveHandler = handleMouseMove
    dragState.mouseUpHandler = handleMouseUp
    dragState.touchMoveHandler = handleTouchMove
    dragState.touchEndHandler = handleTouchEnd
  }, 50)
}

const handleDrag = (event: MouseEvent | TouchEvent, index: number): void => {
  const dragState = dragStates.value[index]
  if (!dragState || !dragState.isDragging) return

  if ('touches' in event) {
    event.preventDefault()
  }

  const currentX = 'touches' in event ? event.touches[0]?.clientX : event.clientX
  const currentY = 'touches' in event ? event.touches[0]?.clientY : event.clientY

  if (currentX === undefined || currentY === undefined) return

  const deltaX = currentX - dragState.startX
  const deltaY = currentY - dragState.startY
  const totalMovement = deltaX - deltaY
  const sizeChange = totalMovement * 0.1
  const newSize = Math.max(1, Math.round(dragState.startSize + sizeChange))

  const brushItem = brushItems.value[index]
  if (brushItem) {
    brushItem.size = newSize
  }
  if (inputValues.value[index] !== undefined) {
    inputValues.value[index] = newSize
  }

  toolStore.setBrushSize(newSize)
  emit('brush-size-changed', newSize, index)

  event.preventDefault()
}

const endDrag = (index: number): void => {
  const dragState = dragStates.value[index]
  if (!dragState) return

  if (dragState.timeout) {
    clearTimeout(dragState.timeout)
    dragState.timeout = null
  }

  dragState.isDragging = false

  if (dragState.mouseMoveHandler) {
    document.removeEventListener('mousemove', dragState.mouseMoveHandler)
  }
  if (dragState.mouseUpHandler) {
    document.removeEventListener('mouseup', dragState.mouseUpHandler)
  }
  if (dragState.touchMoveHandler) {
    document.removeEventListener('touchmove', dragState.touchMoveHandler)
  }
  if (dragState.touchEndHandler) {
    document.removeEventListener('touchend', dragState.touchEndHandler)
  }
}

const handleResetBrushSizes = (): void => {
  const panelConfig = initializeBrushPanel()

  brushItems.value = panelConfig.brushItems
  inputValues.value = panelConfig.inputValues
  selectedIndex.value = panelConfig.selectedIndex

  dragStates.value = panelConfig.brushItems.map(() => ({
    isDragging: false,
    startX: 0,
    startY: 0,
    startSize: 0,
    centerX: 0,
    centerY: 0,
    timeout: null
  }))

  toolStore.setBrushSize(DEFAULT_BRUSH_SIZE)

  emit('brush-item-selected', selectedIndex.value)
  const selectedItem = brushItems.value[selectedIndex.value]
  if (selectedItem) {
    emit('brush-size-changed', selectedItem.size, selectedIndex.value)
  }
}

const handleSelectBrushByIndex = (event: Event): void => {
  const customEvent = event as CustomEvent<CustomEventDetail>
  const { index } = customEvent.detail
  if (index >= 0 && index < brushItems.value.length) {
    selectItem(index)
  }
}

const handleSyncWithToolStore = (): void => {
  if (isInitialized.value) {
    syncWithToolStore()
  }
}

const emitInitialEvents = (): void => {
  emit('brush-item-selected', selectedIndex.value)
  const selectedItem = brushItems.value[selectedIndex.value]
  if (selectedItem) {
    emit('brush-size-changed', selectedItem.size, selectedIndex.value)
  }
}

watch(() => [props.brushCount, props.initialSizes], () => {
  initializeItems()
}, { immediate: true })

watch(inputValues, (newValues) => {
  newValues.forEach((value, index) => {
    const brushItem = brushItems.value[index]
    if (brushItem && value !== undefined && value !== brushItem.size) {
      brushItem.size = value
    }
  })
}, { deep: true })

watch(() => Number(toolStore.currentTool.size), () => {
  if (isInitialized.value) {
    syncWithToolStore()
  }
})

onMounted(() => {
  initializeItems()
  isInitialized.value = true
  emitInitialEvents()

  window.addEventListener('reset-brush-sizes', handleResetBrushSizes)
  window.addEventListener('select-brush-by-index', handleSelectBrushByIndex)
  window.addEventListener('sync-brush-panel-with-tool-store', handleSyncWithToolStore)
})

onUnmounted(() => {
  window.removeEventListener('reset-brush-sizes', handleResetBrushSizes)
  window.removeEventListener('select-brush-by-index', handleSelectBrushByIndex)
  window.removeEventListener('sync-brush-panel-with-tool-store', handleSyncWithToolStore)
})
</script>

<style scoped>
:root {
  --panel-text-size: 12px;
}

.brush-size-panel {
  display: flex;
  align-items: center;
  height: auto;
  min-height: 60px;
  background: transparent;
  gap: 0;
  width: auto;
  min-width: fit-content;
}

.brush-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 44px;
  height: 60px;
  background: transparent;
  cursor: pointer;
  gap: 4px;
  padding: 0;
  position: relative;
}

.brush-item:hover {
  background: theme('colors.creme.DEFAULT');
}

.preview-square {
  width: 44px;
  height: 44px;
  background: transparent;
  position: relative;
  transition: background-color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-square.active-preview {
  background: theme('colors.creme.lighter');
}

.preview-square.draggable {
  cursor: grab;
}

.preview-square.draggable:active {
  cursor: grabbing;
}

.preview-circle {
  background: theme('colors.text.DEFAULT');
  border-radius: 50%;
  min-width: 1px;
  min-height: 1px;
}

.input {
  width: 2rem;
  text-align: center;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  outline: none;
  color: theme('colors.text.DEFAULT');
  font-size: 12px;
  height: 16px;
  line-height: 16px;
  appearance: textfield;
  -moz-appearance: textfield;
}

.input::-webkit-outer-spin-button,
.input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.input:focus {
  background: transparent;
}

.size-text {
  width: 2rem;
  text-align: center;
  font-size: 12px;
  color: theme('colors.text.DEFAULT');
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  height: 16px;
  line-height: 16px;
}
</style>
